import React, { useEffect, useState, useCallback } from'react'
import {
 Sparkles, RefreshCw, ExternalLink, ChevronDown, ChevronUp,
 Lightbulb, Search, BookOpen, Video, ArrowRight, CheckCircle2,
 Loader2, AlertTriangle, Clock, CalendarDays, Target, CheckSquare
} from'lucide-react'
import { Button } from'../components/ui/Button'
import { useMotivatorNudge, type ItemAnalysis, type SearchResult } from'../hooks/useMotivatorNudge'

interface NudgeHistoryEntry {
 id: number
 activity_date: string
 activity_time: string
 description: string | null
 notes: string | null
 extra_data: string | null
}

interface TodayItem {
 id: number
 title: string
 type: string
 status: string
 priority?: string
 scheduled_time?: string
 duration_minutes?: number
 parent_title?: string
}

export const MotivationPage: React.FC = () => {
 const { nudge, hasNudge, loading, error, fetchNudge, fetched } = useMotivatorNudge()
 const [history, setHistory] = useState<NudgeHistoryEntry[]>([])
 const [historyLoading, setHistoryLoading] = useState(false)
 const [expandedItem, setExpandedItem] = useState<number | null>(null)
 const [expandedHistoryItem, setExpandedHistoryItem] = useState<number | null>(null)
 const [todayItems, setTodayItems] = useState<TodayItem[]>([])
 const [todayLoading, setTodayLoading] = useState(false)

 const fetchTodayItems = useCallback(async () => {
 try {
 setTodayLoading(true)
 const today = new Date().toISOString().split('T')[0]
 const res = await fetch(`/api/orchestrator/today-view?date=${today}`, {
 credentials:'include',
 })
 if (res.ok) {
 const data = await res.json()
 // The today-view endpoint may return tasks, action_items, events, etc.
 const items: TodayItem[] = []
 if (data.tasks && Array.isArray(data.tasks)) {
 data.tasks.forEach((t: any) => items.push({
 id: t.id, title: t.title, type:'task', status: t.status ||'pending',
 priority: t.priority, scheduled_time: t.scheduled_time,
 duration_minutes: t.duration_minutes, parent_title: t.parent_title || t.goal_title,
 }))
 }
 if (data.action_items && Array.isArray(data.action_items)) {
 data.action_items.forEach((a: any) => items.push({
 id: a.id, title: a.title || a.description, type:'action_item',
 status: a.status ||'pending', priority: a.priority,
 scheduled_time: a.scheduled_time, parent_title: a.task_title,
 }))
 }
 if (data.events && Array.isArray(data.events)) {
 data.events.forEach((e: any) => items.push({
 id: e.id, title: e.title || e.summary, type:'event',
 status:'scheduled', scheduled_time: e.start_time || e.start,
 duration_minutes: e.duration_minutes,
 }))
 }
 // Also handle flat array response
 if (Array.isArray(data) && !('tasks' in data)) {
 data.forEach((item: any) => items.push({
 id: item.id, title: item.title, type: item.type ||'task',
 status: item.status ||'pending', priority: item.priority,
 scheduled_time: item.scheduled_time, parent_title: item.parent_title,
 }))
 }
 setTodayItems(items)
 }
 } catch {
 // non-critical — today items are supplementary
 } finally {
 setTodayLoading(false)
 }
 }, [])

 useEffect(() => {
 fetchHistory()
 fetchTodayItems()
 }, [])

 const fetchHistory = async () => {
 try {
 setHistoryLoading(true)
 const res = await fetch('/api/orchestrator/nudge-history?limit=20', {
 credentials:'include',
 })
 if (res.ok) {
 const data = await res.json()
 if (data.success) {
 setHistory(data.activities ?? [])
 }
 }
 } catch {
 // non-critical
 } finally {
 setHistoryLoading(false)
 }
 }

 const searchResultCount = nudge
 ? Object.values(nudge.search_results).reduce((sum, arr) => sum + arr.length, 0)
 : 0

 return (
 <div className="space-y-5 max-w-4xl mx-auto">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div className="flex items-baseline gap-2 min-w-0">
 <Sparkles className="h-4 w-4 text-warn self-center shrink-0" />
 <h1 className="text-lg font-bold text-primary">Motivation Boost</h1>
 <p className="hidden md:block text-xs text-muted truncate">
 personalised guidance for your stuck tasks
 </p>
 </div>
 <Button
 onClick={async () => {
 await fetchNudge()
 fetchHistory()
 }}
 disabled={loading}
 className="flex items-center gap-2 bg-warn hover:opacity-90 text-on-brand"
 >
 {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
 {fetched ?'Refresh' :'Generate'}
 </Button>
 </div>

 {/* Today's Schedule */}
 <div className="bg-surface rounded-2xl shadow-sm p-5">
 <h2 className="flex items-center gap-2 text-base font-semibold text-primary mb-3">
 <CalendarDays className="w-5 h-5 text-brand" />
 Today's Schedule
 </h2>
 {todayLoading && (
 <div className="text-center py-4">
 <Loader2 className="w-5 h-5 text-muted animate-spin mx-auto" />
 </div>
 )}
 {!todayLoading && todayItems.length === 0 && (
 <div className="text-center py-4">
 <CheckCircle2 className="w-8 h-8 text-ok mx-auto mb-2" />
 <p className="text-sm text-muted">
 Nothing scheduled for today. A great day for new goals!
 </p>
 </div>
 )}
 {!todayLoading && todayItems.length > 0 && (
 <div className="space-y-2">
 {todayItems.map((item) => (
 <div
 key={`${item.type}-${item.id}`}
 className="flex items-center gap-3 p-3 rounded-lg border border-soft hover:bg-surface-alt transition-colors"
 >
 <div className="flex-shrink-0">
 {item.type ==='event' ? (
 <CalendarDays className="w-4 h-4 text-brand" />
 ) : item.type ==='action_item' ? (
 <CheckSquare className="w-4 h-4 text-ok" />
 ) : (
 <Target className="w-4 h-4 text-warn" />
 )}
 </div>
 <div className="flex-1 min-w-0">
 <p className="text-sm font-medium text-primary truncate">
 {item.title}
 </p>
 <div className="flex items-center gap-2 text-xs text-muted mt-0.5">
 {item.scheduled_time && (
 <span className="flex items-center gap-0.5">
 <Clock className="w-3 h-3" />
 {new Date(item.scheduled_time).toLocaleTimeString([], { hour:'numeric', minute:'2-digit' })}
 </span>
 )}
 {item.duration_minutes && (
 <span>{item.duration_minutes}min</span>
 )}
 {item.parent_title && (
 <span className="text-brand truncate">← {item.parent_title}</span>
 )}
 {item.priority && (
 <span className={`px-1.5 py-0.5 rounded ${
 item.priority ==='high' || item.priority ==='critical'
 ?'bg-bad/10 text-bad'
 :'bg-surface-alt text-secondary'
 }`}>
 {item.priority}
 </span>
 )}
 </div>
 </div>
 <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${
 item.status ==='completed' || item.status ==='done'
 ?'bg-ok/10 text-ok'
 : item.status ==='in_progress'
 ?'bg-brand/10 text-brand'
 :'bg-surface-alt text-secondary'
 }`}>
 {item.status ==='completed' || item.status ==='done' ?'✓' : item.status ==='in_progress' ?'▶' :'○'} {item.status}
 </span>
 </div>
 ))}
 </div>
 )}
 </div>

 {/* Loading state */}
 {loading && (
 <div className="bg-surface rounded-2xl shadow-sm p-8 text-center">
 <Loader2 className="w-10 h-10 text-warn animate-spin mx-auto mb-4" />
 <p className="text-secondary">
 Analysing your tasks, generating insights, and searching for resources...
 </p>
 <p className="text-xs text-muted mt-2">This may take 10-30 seconds</p>
 </div>
 )}

 {/* Error state */}
 {error && !loading && (
 <div className="bg-bad/10 border border-bad/30 rounded-2xl p-4 flex items-start gap-3">
<AlertTriangle className="w-5 h-5 text-bad flex-shrink-0 mt-0.5" />
 <div>
 <p className="font-medium text-bad">Failed to generate nudge</p>
 <p className="text-sm text-bad mt-1">{error}</p>
 </div>
 </div>
 )}

 {/* No stuck items */}
 {fetched && !hasNudge && !loading && (
 <div className="bg-ok/10 border border-ok/30 rounded-2xl p-6 text-center">
<CheckCircle2 className="w-10 h-10 text-ok mx-auto mb-3" />
 <h2 className="font-semibold text-primary">You're on track!</h2>
 <p className="text-sm text-secondary mt-1">
 No stuck items detected. Keep up the great work!
 </p>
 </div>
 )}

 {/* Nudge content */}
 {nudge && hasNudge && !loading && (
 <>
 {/* Overall Assessment */}
 <div className="bg-surface rounded-2xl shadow-sm p-5">
 <h2 className="flex items-center gap-2 text-base font-semibold text-primary mb-3">
 <Lightbulb className="w-5 h-5 text-warn" />
 Overall Assessment
 </h2>
 <p className="text-secondary leading-relaxed">
 {nudge.overall_message}
 </p>
 {nudge.horton_wisdom && (
 <div className="mt-3 p-3 bg-warn/10 rounded-lg border border-warn/30">
 <p className="text-sm text-warn italic">
 {nudge.horton_wisdom}
 </p>
 </div>
 )}
 </div>

 {/* Items Analysis */}
 {nudge.items_analysis.length > 0 && (
 <div className="bg-surface rounded-2xl shadow-sm p-5">
 <h2 className="flex items-center gap-2 text-base font-semibold text-primary mb-3">
 <AlertTriangle className="w-5 h-5 text-warn" />
 Stuck Items ({nudge.items_analysis.length})
 </h2>
 <div className="space-y-3">
 {nudge.items_analysis.map((item: ItemAnalysis, idx: number) => (
 <div
 key={idx}
 className="border border-soft rounded-xl overflow-hidden"
 >
 <button
 className="w-full p-4 text-left flex items-center justify-between hover:bg-surface-alt transition-colors"
 onClick={() => setExpandedItem(expandedItem === idx ? null : idx)}
 >
 <div className="flex-1 min-w-0">
 <p className="font-medium text-primary truncate">
 {item.title}
 </p>
 <p className="text-sm text-muted mt-0.5">
 {item.stuck_reason}
 {item.days_stuck > 0 && (
 <span className="ml-2 text-xs px-1.5 py-0.5 bg-warn/10 text-warn rounded">
 {item.days_stuck}d stuck
 </span>
 )}
 </p>
 </div>
 {expandedItem === idx
 ? <ChevronUp className="w-4 h-4 text-muted flex-shrink-0" />
 : <ChevronDown className="w-4 h-4 text-muted flex-shrink-0" />
 }
 </button>

 {expandedItem === idx && (
 <div className="px-4 pb-4 space-y-3 border-t border-soft pt-3">
 {/* Encouragement */}
 {item.encouragement && (
 <p className="text-sm text-warn italic">
 {item.encouragement}
 </p>
 )}

 {/* Micro-steps */}
 {item.micro_steps?.length > 0 && (
 <div>
 <p className="text-xs font-semibold text-muted uppercase tracking-wider mb-2">
 Suggested micro-steps
 </p>
 <ol className="space-y-1.5">
 {item.micro_steps.map((step, sIdx) => (
 <li key={sIdx} className="flex items-start gap-2 text-sm text-secondary">
 <span className="flex-shrink-0 w-5 h-5 rounded-full bg-warn/10 text-warn flex items-center justify-center text-xs font-medium mt-0.5">
 {sIdx + 1}
 </span>
 {step}
 </li>
 ))}
 </ol>
 </div>
 )}
 </div>
 )}
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Web Resources */}
 {searchResultCount > 0 && (
 <div className="bg-surface rounded-2xl shadow-sm p-5">
 <h2 className="flex items-center gap-2 text-base font-semibold text-primary mb-3">
 <Search className="w-5 h-5 text-brand" />
 Web Resources ({searchResultCount})
 </h2>
 <div className="space-y-4">
 {Object.entries(nudge.search_results).map(([query, results]) => (
 <div key={query}>
 <p className="text-xs font-medium text-muted uppercase tracking-wider mb-2">
 {query}
 </p>
 <div className="space-y-2">
 {(results as SearchResult[]).map((r, rIdx) => (
 <a
 key={rIdx}
 href={r.url}
 target="_blank"
 rel="noopener noreferrer"
 className="block p-3 rounded-lg border border-soft hover:border-brand/30 hover:bg-brand/5 transition-colors"
 >
 <div className="flex items-start gap-2">
 <div className="flex-1 min-w-0">
 <p className="text-sm font-medium text-brand truncate">
 {r.title}
 </p>
 <p className="text-xs text-secondary mt-0.5 line-clamp-2">
 {r.snippet}
 </p>
 </div>
 <div className="flex items-center gap-1 flex-shrink-0">
 {r.is_video && (
 <span className="text-xs px-1.5 py-0.5 bg-bad/10 text-bad rounded">
 <Video className="w-3 h-3 inline mr-0.5" />Video
 </span>
 )}
 {r.is_tutorial && (
 <span className="text-xs px-1.5 py-0.5 bg-ok/10 text-ok rounded">
 <BookOpen className="w-3 h-3 inline mr-0.5" />Tutorial
 </span>
 )}
 <ExternalLink className="w-3.5 h-3.5 text-muted" />
 </div>
 </div>
 </a>
 ))}
 </div>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Suggested Action */}
 <div className="bg-warn/10 rounded-2xl border border-warn/30 p-5">
 <h2 className="flex items-center gap-2 text-base font-semibold text-primary mb-2">
 <ArrowRight className="w-5 h-5 text-warn" />
 What to Do Next
 </h2>
 <p className="text-secondary">
 {nudge.nudge?.suggested_action}
 </p>
 {nudge.nudge?.follow_up_question && (
 <p className="text-sm text-warn mt-3 italic">
 {nudge.nudge.follow_up_question}
 </p>
 )}
 {nudge.nudge?.final_message && (
 <div className="mt-3 pt-3 border-t border-warn/30">
 <p className="text-sm text-secondary">
 {nudge.nudge.final_message}
 </p>
 </div>
 )}
 </div>
 </>
 )}

 {/* Nudge History */}
 <div className="bg-surface rounded-2xl shadow-sm p-5">
 <h2 className="flex items-center gap-2 text-base font-semibold text-primary mb-3">
 <Clock className="w-5 h-5 text-muted" />
 Past Motivation Boosts
 </h2>
 {historyLoading && (
 <div className="text-center py-4">
 <Loader2 className="w-5 h-5 text-muted animate-spin mx-auto" />
 </div>
 )}
 {!historyLoading && history.length === 0 && (
 <p className="text-sm text-muted py-4 text-center">
 No past nudges yet. Click"Generate" above to get your first motivation boost.
 </p>
 )}
 {!historyLoading && history.length > 0 && (
 <div className="space-y-2">
 {history.map((entry, idx) => {
 let extra: Record<string, unknown> | null = null
 try {
 extra = entry.extra_data ? JSON.parse(entry.extra_data) : null
 } catch { /* ignore */ }

 return (
 <div
 key={entry.id ?? idx}
 className="border border-soft rounded-xl overflow-hidden"
 >
 <button
 className="w-full p-3 text-left flex items-center justify-between hover:bg-surface-alt transition-colors"
 onClick={() => setExpandedHistoryItem(expandedHistoryItem === idx ? null : idx)}
 >
 <div className="flex-1 min-w-0">
 <p className="text-sm font-medium text-primary truncate">
 {entry.description ||'Motivation Boost'}
 </p>
 <p className="text-xs text-muted mt-0.5">
 {entry.activity_date} at {entry.activity_time?.split('.')[0] ||''}
 </p>
 </div>
 {expandedHistoryItem === idx
 ? <ChevronUp className="w-4 h-4 text-muted" />
 : <ChevronDown className="w-4 h-4 text-muted" />
 }
 </button>
 {expandedHistoryItem === idx && extra && (
 <div className="px-3 pb-3 border-t border-soft pt-3 space-y-2">
 {entry.notes && (
 <p className="text-sm text-warn">
 Suggested: {entry.notes}
 </p>
 )}
 {(extra.horton_wisdom as string) && (
 <p className="text-xs text-secondary italic">
 {extra.horton_wisdom as string}
 </p>
 )}
 {Array.isArray(extra.items_analysis) && (extra.items_analysis as Array<Record<string, unknown>>).length > 0 && (
 <div className="text-xs text-muted">
 Items analysed: {(extra.items_analysis as Array<Record<string, unknown>>).map(
 (i) => (i as Record<string, unknown>).title as string
 ).filter(Boolean).join(',')}
 </div>
 )}
 </div>
 )}
 </div>
 )
 })}
 </div>
 )}
 </div>
 </div>
 )
}

export default MotivationPage
